import { List } from "immutable";
import _ from "lodash";
import React from "react";
import * as Editors from "./editors";
import * as Comp from "../../../components";
import { Paths } from "../../../constants";
import { Statuses } from "../../../domain";
import { ContentBlockTypes } from "../../../domain/ContentBlockTypes";
import * as Forms from "../../../forms";
import * as M from "../../../models";
import { Utility } from "../../../utils";

interface OwnProps {
    navItems: List<M.AdminNav.NavListItem>;
    onSubmit: (model: Forms.AdminPage.PageEditData, body: any) => void;
}

interface OwnState {
    formModel: Forms.AdminPage.PageEdit;
}

export class EditForm extends Comp.Validation.ComponentWithValidatedForm<OwnProps, OwnState> {

    public constructor(props: OwnProps) {
        super(props);

        const formModel = new Forms.AdminPage.PageEdit();
        this.state = this.getInitialState(formModel);
    }

    public setEditContext(page?: M.Page.Page) {
        if (page) {
            const model = {
                ...this.state.formModel.data,
            } as Forms.AdminPage.PageEditData;

            model.id = page.id;
            model.active = Statuses.isActive(page.statusId);
            model.title = page.title || "";

            page.contentBlocks.map((cb, i) => {
                const cbForm: Forms.AdminPage.ContentBlockData = {
                    deleted: false,
                    id: cb.id,
                    index: i,
                    label: cb.label,
                    typeId: cb.typeId,
                    type: cb.type,
                };

                if (cb.typeId === ContentBlockTypes.Text.id) {
                    const textBlock = cb as M.Page.ContentText;
                    model.contentBlocks.push({
                        ...cbForm,
                        body: textBlock.body,
                    } as Forms.AdminPage.ContentTextData);
                }
                else if (cb.typeId === ContentBlockTypes.Carousel.id) {
                    const carouselBlock = cb as M.Page.ContentCarousel;
                    model.contentBlocks.push({
                        ...cbForm,
                        slideSpeed: carouselBlock.slideSpeed,
                        slides: _.sortBy(carouselBlock.slides, "sort").map((s, slideIdx) => ({
                            ...s,
                            delete: false,
                            index: slideIdx,
                            sort: slideIdx + 1, // assign sequential sort in case not sequential in db
                        })),
                    } as Forms.AdminPage.ContentCarouselData);
                }
                else if (cb.typeId === ContentBlockTypes.Hero.id) {
                    const heroBlock = cb as M.Page.ContentHero;
                    model.contentBlocks.push({
                        ...cbForm,
                        body: heroBlock.body,
                        image: heroBlock.image,
                    } as Forms.AdminPage.ContentHeroData);
                }
                else if (cb.typeId === ContentBlockTypes.LinkList.id) {
                    const linkListBlock = cb as M.Page.ContentLinkList;
                    model.contentBlocks.push({
                        ...cbForm,
                        links: _.sortBy(linkListBlock.links, "sort").map((lnk, lnkIdx) => {
                            const link: Forms.AdminPage.ContentLinkData = {
                                deleted: false,
                                icon: lnk.icon,
                                iconImage: lnk.iconImage,
                                id: lnk.id,
                                index: lnkIdx,
                                linksToNav: lnk.linksToNavItem !== null && lnk.linksToNavItem !== undefined
                                    ? Utility.toReactSelectOption(lnk.linksToNavItem, lnk.linksToNavItem.title, lnk.linksToNavItem.id)
                                    : null,
                                linksToUrl: lnk.linksToUrl,
                                linkType: lnk.linksToNavId ? Forms.AdminPage.LINK_TYPE_PAGE : Forms.AdminPage.LINK_TYPE_URL,
                                promptThumbnail: linkListBlock.promptThumbnail,
                                sort: lnkIdx + 1, // assign sequential sort in case not sequential in db
                                thumbnailType: lnk.iconImage ? Forms.AdminPage.THUMBNAIL_TYPE_IMAGE : Forms.AdminPage.THUMBNAIL_TYPE_ICON,
                                title: lnk.title,
                            };
                            return link;
                        }),
                        promptThumbnail: linkListBlock.promptThumbnail,
                        title: linkListBlock.title,
                    } as Forms.AdminPage.ContentLinkListData);
                }
            });

            const formModel = { ...this.state.formModel };
            formModel.data = model;

            this.setState({
                ...this.state,
                formModel,
            });
        }
    }

    public render() {
        const model = this.state.formModel.data as Forms.AdminPage.PageEditData;
        const contentBlockEditors = model.contentBlocks.map((cb, i) => {
            const label = Utility.toSentenceCase(cb.label);
            if (cb.deleted)
                return null;
            else if (cb.typeId === ContentBlockTypes.Carousel.id)
                return <Editors.ContentCarouselEditor
                    key={i}
                    contentBlockId={cb.id}
                    formComponent={this}
                    label={label}
                    namePrefix={`contentBlocks[${i}].`}
                    onSlideAdd={(cbId) => this.onCarouselSlideAdd(cbId)}
                    onSlideDelete={(cbId, slideIdx) => this.onCarouselSlideDelete(cbId, slideIdx)}
                    slides={(cb as Forms.AdminPage.ContentCarouselData).slides}
                />;
            else if (cb.typeId === ContentBlockTypes.Hero.id)
                return <Editors.ContentHeroEditor
                    key={i}
                    formComponent={this}
                    label={label}
                    namePrefix={`contentBlocks[${i}].`}
                />;
            else if (cb.typeId === ContentBlockTypes.LinkList.id)
                return <Editors.ContentLinkListEditor
                    key={i}
                    contentBlockIndex={cb.index}
                    formComponent={this}
                    label={label}
                    namePrefix={`contentBlocks[${i}].`}
                    navItems={this.props.navItems}
                    links={(cb as Forms.AdminPage.ContentLinkListData).links}
                    promptThumbnail={(cb as Forms.AdminPage.ContentLinkListData).promptThumbnail}
                />;
            else if (cb.typeId === ContentBlockTypes.Text.id)
                return <Editors.ContentTextEditor
                    key={i}
                    formComponent={this}
                    label={label}
                    namePrefix={`contentBlocks[${i}].`}
                />;
            else
                return null;
        });

        return <Comp.Validation.ValidatedForm
            formComponent={this}
            model={this.state.formModel.data}
            onValidSubmit={(m) => this.handleSubmit(m)}
            schema={this.state.formModel.rules}
            ref={(ref) => this.form = ref}
        >

            {/* <Comp.Editors.BooleanField formComponent={this} name="active" />
            <Comp.Editors.BooleanField formComponent={this} name="hasHero" onInputChange={(fieldName, value) => this.onHeroCheck(fieldName)} />
            <Comp.Editors.BooleanField formComponent={this} name="hasFeaturedPages" onInputChange={(fieldName, value) => this.onFeaturedPageListCheck(fieldName)} /> */}
            <Comp.Editors.TextField formComponent={this} name="title" />

            {contentBlockEditors}

            <Comp.Editors.SubmitField cancelLink={Paths.AdminPage.List} showProcessing={this.state.formProcessing} />

        </Comp.Validation.ValidatedForm>;
    }

    private handleSubmit(model: Forms.AdminPage.PageEditData) {
        const body = {
            id: model.id,
            title: model.title,
            textBlocks: model.contentBlocks.filter((cb) => cb.typeId === ContentBlockTypes.Text.id),
        };
        this.props.onSubmit(model, body);
    }

    private onCarouselSlideAdd(contentBlockId: number) {
        const formModel = { ...this.state.formModel } as Forms.AdminPage.PageEdit;
        const carousel = _.find(formModel.data.contentBlocks,
            (cb) => (cb.id === contentBlockId && cb.typeId === ContentBlockTypes.Carousel.id)) as Forms.AdminPage.ContentCarouselData;

        if (carousel) {
            const slide: Forms.AdminPage.ContentCarouselSlideData = {
                delete: false,
                id: 0,
                index: carousel.slides.length,
                sort: (_.max(carousel.slides.filter((s) => !s.delete).map((s) => s.sort)) || 0) + 1,
            };
            carousel.slides.push(slide);
            this.setState({ ...this.state, formModel });
        }
    }

    private onCarouselSlideDelete(contentBlockId: number, slideIdx: number) {
        const formModel = { ...this.state.formModel } as Forms.AdminPage.PageEdit;
        const carousel = _.find(formModel.data.contentBlocks,
            (cb) => (cb.id === contentBlockId && cb.typeId === ContentBlockTypes.Carousel.id)) as Forms.AdminPage.ContentCarouselData;

        if (carousel) {
            const slide = _.find(carousel.slides, (s, i) => i === slideIdx);
            if (slide)
                slide.delete = true;
            this.setState({ ...this.state, formModel });
        }
    }

    // private onFeaturedPageListCheck(inputName: string) {
    //     const label = "FeaturedPages";
    //     const isChecked = !this.getFieldValue(inputName);
    //     const formModel = { ...this.state.formModel } as Forms.AdminPage.PageEdit;
    //     const listIdx = _.findIndex(formModel.data.contentBlocks, (cb) => (cb.typeId === ContentBlockTypes.LinkList.id && cb.label === label));
    //     let list: Forms.AdminPage.ContentLinkListData | undefined;

    //     if (listIdx > -1)
    //         list = formModel.data.contentBlocks[listIdx] as Forms.AdminPage.ContentLinkListData;

    //     if (isChecked) {
    //         if (list)
    //             list.deleted = false;
    //         else {
    //             list = {
    //                 deleted: false,
    //                 id: 0,
    //                 index: 0,
    //                 label,
    //                 links: [{ ...Editors.ContentLinkList_DefaultLink }],
    //                 promptThumbnail: false,
    //                 type: ContentBlockTypes.LinkList.type,
    //                 typeId: ContentBlockTypes.LinkList.id,
    //             };
    //             formModel.data.contentBlocks.splice(0, 0, list);
    //         }
    //     }
    //     else {
    //         if (list) {
    //             list.deleted = true;
    //         }
    //     }

    //     // reset cb indexes before setting state
    //     formModel.data.contentBlocks.map((cb, i) => { cb.index = i; });
    //     this.setState({ ...this.state, formModel });
    // }

    // private onHeroCheck(inputName: string) {
    //     const label = "Hero";
    //     const isChecked = !this.getFieldValue(inputName);
    //     const formModel = { ...this.state.formModel } as Forms.AdminPage.PageEdit;
    //     const heroIdx = _.findIndex(formModel.data.contentBlocks, (cb) => (cb.typeId === ContentBlockTypes.Hero.id && cb.label === label));
    //     let hero: Forms.AdminPage.ContentHeroData | undefined;

    //     if (heroIdx > -1)
    //         hero = formModel.data.contentBlocks[heroIdx] as Forms.AdminPage.ContentHeroData;

    //     if (isChecked) {
    //         if (hero)
    //             hero.deleted = false;
    //         else {
    //             hero = {
    //                 deleted: false,
    //                 id: 0,
    //                 index: 0,
    //                 label,
    //                 type: ContentBlockTypes.Hero.type,
    //                 typeId: ContentBlockTypes.Hero.id,
    //             };
    //             formModel.data.contentBlocks.splice(0, 0, hero);
    //         }
    //     }
    //     else {
    //         if (hero) {
    //             hero.deleted = true;
    //         }
    //     }

    //     // reset cb indexes before setting state
    //     formModel.data.contentBlocks.map((cb, i) => { cb.index = i; });
    //     this.setState({ ...this.state, formModel });
    // }
}



// WEBPACK FOOTER //
// ./src/containers/adminPage/edit/EditForm.tsx